import { ErrorService, Response, ServerErrorStatus, Vehicle } from '@agilox/common';
import { AuthService, userInitializationFactory } from '@auth';
import { UserStoreService } from '@user-store';
import { inject } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppSettings } from '../interfaces/app-settings.interface';
import { VehiclesService } from '../shared/services/vehicles/vehicles.service';
import {
	collectVehicleSerials,
	pruneInaccessibleVehicles,
} from './prune-inaccessible-vehicles.util';

/**
 * Initializes the Analytics application with MSAL authentication,
 * user store synchronization, and vehicle permission validation
 * @returns Promise that resolves when initialization is complete
 */
export function initializeAnalyticsApp(
	authService: AuthService,
	userStore: UserStoreService,
	vehicleService: VehiclesService
): Promise<any> {
	const errorService = inject(ErrorService);
	return firstValueFrom(
		userInitializationFactory(authService.msalInstance, userStore)().pipe(
			switchMap((user) => {
				const settings = userStore.appSettings();
				// The user or its settings can be missing when the backend errored while
				// loading the current user. Skip the vehicle check so the app still
				// bootstraps and can render the error page.
				if (!user || !settings) return of(null);
				return checkVehiclePermissions(settings, vehicleService, userStore);
			})
		)
	).then((result) => {
		// Analytics' error interceptor swallows failed requests into an empty
		// response, so a failed current-user load surfaces here as a user without
		// an id rather than as a thrown error. Treat that as a server error.
		if (!userStore.currentUser()?.id && !errorService.serverError()) {
			errorService.setServerError(ServerErrorStatus.SERVER_ERROR);
		}
		return result;
	});
}

function checkVehiclePermissions(
	settings: AppSettings,
	vehicleService: VehiclesService,
	userStore: UserStoreService
): Observable<AppSettings> {
	const vehicleSerialsToCheck = collectVehicleSerials(settings);

	if (vehicleSerialsToCheck.length === 0) {
		return of(settings);
	}

	// Fetch all vehicles the user has access to
	return vehicleService.loadVehiclesResponse(vehicleSerialsToCheck).pipe(
		switchMap((response: Response<Vehicle>) => {
			// The error interceptor swallows a failed request into an empty response, so
			// an unsuccessful one cannot be told apart from "none of the serials resolved".
			// Pruning on it would drop the whole selection on a transient backend failure.
			if (!isSuccessfulResponse(response)) {
				return of(settings);
			}

			const accessibleSerials = new Set(response.data.map((vehicle) => vehicle.serial));
			const prunedSettings = pruneInaccessibleVehicles(settings, accessibleSerials);
			if (!prunedSettings) {
				return of(settings);
			}

			return userStore.updateAppSettings(prunedSettings).pipe(
				map(() => prunedSettings),
				catchError(() => of(prunedSettings))
			);
		})
	);
}

function isSuccessfulResponse(response: Response<Vehicle>): boolean {
	return !!response?.status && Array.isArray(response.data);
}
